import { FC, useCallback, useContext, useEffect, useState } from "react";
import { DocumentsService } from "../../../../../services/document/documentsService";
import { toast } from "sonner";
import { StopsService } from "../../../../../services/preventive-stops/stopsService";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import { useParams } from "react-router-dom";
import useStopsStatus from "../../../../../hooks/api-calls/useStopsStatus";
import useFetch from "../../../../../hooks/useFetch";
import CustomTable from "../../../../../components/table/CustomTable";
import StatusDropdown from "../../../../../components/forms/StatusDropdown";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { Link } from "react-router-dom";
import { menuRoutes } from "../../../../../router/menu";
import { HiOutlineEye } from "react-icons/hi2";
import { MdOutlineInsertPhoto, MdOutlineModeEditOutline } from "react-icons/md";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import { ListApiResponse } from "../../../../../type/entities/apiResponse-type";
import StopsFormModal from "../../../../preventive-stops/components/StopFormModal";
import useFilters from "../../../../../hooks/useFilters";
import moment from "moment";
import cn from "../../../../../utils/classNames";
import Viewer from "react-viewer";
import { PrivilegeContext } from "../../../../../components/priviledge/PriviledgeProvider";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { FaSpinner } from "react-icons/fa";

const columnOrder = [
    'id',
    'stopCode',
    'stopDate',
    'park',
    'numMachines',
    'speciesPreventiveStops',
    'preventiveStopsStatus',
    'stopWithApp',
    'downtimePerMachine',
    'preventiveStopsPhotos'
]

const ParkStops: FC<{}> = () => {

    // STATES

    const [ stop , setStop ] = useState<any>(null);
    const [isViewerOpen, setIsViewerOpen] = useState(null);
    const [openModalForm, setIsOpenModalForm] = useState<boolean>(false);
    const [ isLoadingImages, setIsLoadingImages ] = useState<boolean>(false);
    const [ isValidating, setIsValidating ] = useState<boolean>(false);
    const [ viewerImages, setViewerImages] = useState<any>(null);

    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { userCan } = useContext(PrivilegeContext);
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();
    const service = new StopsService();
    const { id = '' } = useParams<{ id: string }>();
    const { getStopsStatusList } = useStopsStatus();

    // FETCH DATA

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
            if (filters?.filter_filters?.park !== id) return null;
            const response = await service.listStops(filters);
            return response.getResponseData();
    },[filters]));
    
    // FUNCTIONS

    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR UNA PARADA
     * @EN DELETE A STOP
     * @param id
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteStop(id)).getResponseData();
        if(response.success) {
            toast.success("Parada eliminada correctamente");
            refetch();
        }else {
            handleErrors(response);
        }
    };
    //----------------------------------------------------------------------------------------------------------------------------
    
    
    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA TODAS LAS PARADAS SELECCIONADAS
     * @EN DELETES ALL SELECTED STOPS
     * 
     * @param ids
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiStops(ids)).getResponseData();
        if(response.success) {
            toast.success(response.message);
            refetch();
        }else {
            handleErrors(response);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------------
        
    
    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ORDENA LOS DATOS PARA QUE COINCIDAN CON LA COLUMNA
     * @EN SORTS THE DATA TO MATCH THE COLUMN
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    }) || [];
    //-----------------------------------------------------------------------------------------------------------------------
    
    
    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO DE LA PARADA
     * @EN CHANGE THE STATUS OF THE STOP
     * 
     * @param id
     * @param status
     */ 
    //-----------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await service.changeStatusStop(id, status)).getResponseData();
        if(response.success) {
            toast.success("Estado de la parada cambiado correctamente");
            refetch();
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------
        
    
    
    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR MODAL
     * @EN CLOSE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose =  () => {
        setIsOpenModalForm(false);
        setTimeout(() => {  
            setStop(null);
        }
        , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------
    
    const handleOpenViewer = async (stopRow : any) => {
        setIsLoadingImages(true);
        if (!stopRow.preventiveStopsPhotos || stopRow.preventiveStopsPhotos.length === 0) return;
        const images = await Promise.all(
            (stopRow.preventiveStopsPhotos || []).map(async (photo : any) => {
                const response = await new DocumentsService().renderDocument(photo.document.id);
                const file = new Blob([response?.getResponse()?.data], { type: 'image/' + photo.document.extension });
                return {
                    ...photo,
                    src: URL.createObjectURL(file),
                };
            })
        );
        setViewerImages(images);
        setIsViewerOpen(stopRow.id);
        setIsLoadingImages(false);
    };

    useEffect(() => {
        if (id) {
            resetFilters();
            updateFilters({ park: id });
        }
    }, [id]);

    // RENDER

    return (
        <div>
            <div className="mx-auto w-100 max-h-[auto] overflow-auto">
                <div className="grid grid-cols-1 gap-4">
                    <div className="md:col-span-3 col-span-1">
                        <div className="grid gap-7">
                                <CustomTable 
                                id={'park-stops-list'}
                                divClassName='max-h-table-park'
                                classNameTable='fs-table'
                                headerTableClassName='fs-table-header'
                                columnOrder={columnOrder}
                                data={orderedData()}
                                isLoading={loading}
                                columnsNotShown={[]}
                                columnsNoSortables={[]}
                                multiActions={[
                                    {
                                        label: 'Validar seleccionados',
                                        render: (selectedRows: any, selectedRowsOriginal: any, reset: Function) => {
                                            return (
                                                <Button
                                                    variant="outline"
                                                    color="primary"
                                                    className="flex items-center border-green text-green"   
                                                    disabled={isValidating}
                                                    onClick={async () => {
                                                        try {
                                                            setIsValidating(true);
                                                            const response = (await service.validateSelectedStops(selectedRows));
                                                            if (response) {
                                                                toast.success('Paradas validadas correctamente');
                                                                refetch();
                                                            } else {
                                                                toast.error('Error al validar las paradas');
                                                            }
                                                        } catch (error: any) {
                                                            toast.error(error.message);
                                                        } finally {
                                                            setIsValidating(false);
                                                            reset();
                                                        }
                                                    }}
                                                >
                                                    <IoCheckmarkDoneSharp className="h-[20px] w-[20px] mr-2" />
                                                    {isValidating ? <FaSpinner className="animate-spin h-[20px] w-[20px] mr-2" /> : <span>Validar</span>}
                                                </Button>
                                            )
                                        }
                                    }
                                ]}
                                isSortable
                                addicionalButtons={
                                    <div className="flex items-center gap-2">
                                        <Button
                                            color="primary"
                                            onClick={() => {  setIsOpenModalForm(true); }}
                                            className={cn(['mt-4 md:mt-0'])}
                                        >
                                            Crear Parada
                                        </Button>
                                    </div>  
                                }
                                overrideColumns={[
                                    {
                                        key: 'stopCode',
                                        customHeader: 'Código',
                                        render: (row: any) => {
                                            return ( <a href={`${menuRoutes.stops.path}/${row.row.original.id}/profile/${menuRoutes.stops.profile.info}`} className='text-primary hover:text-primary hover:underline'>
                                                {row.row.original.stopCode || '-'}
                                            </a> );
                                        }
                                    },
                                    {
                                        key: 'stopDate',
                                        customHeader: 'Fecha',
                                        render: (row: any) => {
                                            return row.row.original.stopDate ? moment(row.row.original.stopDate.date).format('DD/MM/YYYY') : '-';
                                        }
                                    },
                                    {
                                        key: 'park',
                                        customHeader: 'Parque Eólico',
                                        render: (row: any) => {
                                            return row.row.original.park?.name || '-';
                                        }
                                    },
                                    {
                                        key: 'speciesPreventiveStops',
                                        customHeader: 'Especie de Parada',
                                        render: (row: any) => {
                                            return (
                                                <div>
                                                    {
                                                        row.row.original.speciesPreventiveStops?.map((species: any) => (
                                                            <div key={species.species.id}>
                                                                <i className="italic">{species.species.scientificName}</i> ({species.numIndividuals})
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            )
                                        }
                                    },
                                    {
                                        key: 'preventiveStopsStatus',
                                        customHeader: 'Estado',
                                        render: (row: any) => {
                                            const stopStatus = row.row.original?.preventiveStopsStatus;
                                            const stopId = row.row.original?.id;
                                            return (
                                                <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id + stopStatus.id}>
                                                    {(stopId && stopStatus) ? (
                                                        <StatusDropdown
                                                            divClassName="fs-table"
                                                            optionsClassName="fs-table"
                                                            selectedClassName="fs-table"
                                                            entityId={row.row.original?.id}
                                                            title="¿Estas seguro de cambiar el estado de la parada?"
                                                            statesOptions={getStopsStatusList()}
                                                            currentState={stopStatus}
                                                            handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(entityId, statusId)}
                                                            hasComments={false}
                                                            disabled={!userCan('validate', 'preventive_stops')}
                                                        />
                                                    ) : "N/A"}
                                                </div>
                                            );
                                        }
                                    },
                                    {
                                        key: 'stopWithApp',
                                        customHeader: 'Parado con la APP',
                                        render: (row: any) => {
                                            const translations: { [key: string]: string } = {
                                                yes: 'Sí',
                                                no: 'No',
                                                partial: 'Parcial',
                                            };
                                            return translations[row.row.original.stopWithApp] || '';
                                        }
                                    },
                                    {
                                        key: 'downtimePerMachine',
                                        render: (row: any) => {
                                            return row.row.original.downtimePerMachine + ' min' || '-';
                                        }  
                                    },
                                    {
                                        key: 'preventiveStopsPhotos',
                                        customHeader: 'Fotos',
                                        render: (row: any) => {
                                            return (
                                                <p className="flex items-center gap-2">
                                                <MdOutlineInsertPhoto
                                                    className="h-4 w-4"
                                                    size={20}
                                                    onClick={() => handleOpenViewer(row.row.original)}
                                                />
                                                <span>{row.row.original.preventiveStopsPhotos?.length || 0}</span>
                                                {row.row.original.preventiveStopsPhotos?.length !== 0 ? <Viewer
                                                    visible={isViewerOpen === row.row.original.id}
                                                    onClose={() => setIsViewerOpen(null)}
                                                    images={viewerImages?.map((img: any) => ({ src: img.src, alt: img.document.name }))}
                                                />
                                                : null}
                                            </p>
                                            );
                                        }
                                    }
                                ]}
                                actions={[
                                    {
                                        label: 'View',
                                        permissions: { group: 'preventive_stops', permission: 'get' },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Ver paradas'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <Link
                                                        to={`${menuRoutes.stops.path}/${row.id}/profile/${menuRoutes.stops.profile.info}`}>
                                                        <ActionIcon
                                                            as="span"
                                                            size="sm"
                                                            variant="outline"
                                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-5 h-5"
                                                        >
                                                            <HiOutlineEye className="h-3 w-3" size={20} />
                                                        </ActionIcon>
                                                    </Link>
                                                </Tooltip>
                                            );
                                        },
                                    },
                                    {
                                        label: 'Edit',
                                        permissions: { group: 'preventive_stops', permission: 'edit' },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Editar parada'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <ActionIcon
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                                        onClick={() => {
                                                            setIsOpenModalForm(true)
                                                            setStop(row.id);
                                                        }}
                                                    >
                                                        <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            );
                                        },
                                    },  
                                    {
                                        label: 'Delete',
                                        permissions: { group: 'preventive_stops', permission: 'delete' },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Eliminar parada'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <div>
                                                        <DeletePopover
                                                            actionIconClassName="w-5 h-5"
                                                            title={`Eliminar parada`}
                                                            description={`¿Estás seguro de que deseas eliminar la parada ${row.id}?`}
                                                            onDelete={() => handleDelete(row.id)}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            );
                                        },
                                    },
                                ]}
                                filters={filters}
                                updateFilters={updateFilters}
                                handleMultipleDelete={handleMultiDelete}
                                updateFilterOrder={updateFilterOrder}
                                defaultOrder={filters.filter_order || undefined}
                                paginationData={{
                                    pageSize: filters.limit,
                                    currentPage: filters.page,
                                    pageCount: (data as ListApiResponse) ? data.lastPage : 0,
                                    totalCount: data?.totalRegisters,
                                    handlePagination: updatePage,
                                    handlePerPage: updatePageSize,
                                }}
                            />

                            <StopsFormModal
                                isOpen ={openModalForm}
                                onClose ={onClose}
                                stop ={stop}
                                refetch ={refetch}
                                isFromPark
                                parkId={id}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ParkStops;